import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";

export type AuditLogRow = {
  id: string;
  occurredAt: string | null;
  userId: string | null;
  userEmail: string | null;
  userDisplayName: string | null;
  userRole: string | null;
  action: string;
  module: string | null;
  summary: string;
  httpMethod: string | null;
  httpPath: string | null;
  statusCode: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  resourceId: string | null;
  details: unknown;
};

export type AuditLogFilters = {
  limit?: number;
  offset?: number;
  action?: string;
  module?: string;
  userId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
};

export type AuditLogResponse = {
  total: number;
  limit: number;
  offset: number;
  rows: AuditLogRow[];
};

export type AuditLogFilterOptions = {
  actions: string[];
  modules: string[];
  users: { id: string; email: string; displayName: string }[];
};

function queryString(filters: AuditLogFilters) {
  const q = new URLSearchParams();
  if (filters.limit != null) q.set("limit", String(filters.limit));
  if (filters.offset != null) q.set("offset", String(filters.offset));
  if (filters.action?.trim()) q.set("action", filters.action.trim());
  if (filters.module?.trim()) q.set("module", filters.module.trim());
  if (filters.userId?.trim()) q.set("userId", filters.userId.trim());
  if (filters.search?.trim()) q.set("search", filters.search.trim());
  if (filters.dateFrom?.trim()) q.set("dateFrom", filters.dateFrom.trim());
  if (filters.dateTo?.trim()) q.set("dateTo", filters.dateTo.trim());
  const s = q.toString();
  return s ? `?${s}` : "";
}

async function parseError(res: Response) {
  const j = (await res.json().catch(() => ({}))) as { error?: string };
  return j.error || `Request failed (${res.status})`;
}

export async function fetchAuditLog(filters: AuditLogFilters = {}): Promise<AuditLogResponse> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/audit-log${queryString(filters)}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<AuditLogResponse>;
}

export async function fetchAuditLogFilterOptions(): Promise<AuditLogFilterOptions> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/audit-log/filters`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<AuditLogFilterOptions>;
}

export async function postAuthLogout(): Promise<void> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/auth/logout`, { method: "POST" });
  if (!res.ok) throw new Error(await parseError(res));
}

export async function postClientAuditEvent(payload: {
  eventType?: string;
  path: string;
  label?: string;
  details?: unknown;
}): Promise<void> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/audit-log/client-event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await parseError(res));
}

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  login: "Sign in",
  login_failed: "Sign in failed",
  logout: "Sign out",
  password_change: "Password change",
  page_view: "Page opened",
  client_action: "UI action",
  view: "View",
  create: "Create",
  update: "Update",
  delete: "Delete",
  export: "Export",
  import: "Import",
  other: "Other",
};
